import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ordersApi } from "../../api/orders.js";
import { usersApi } from "../../api/users.js";
import RowsPerPageSelect from "../../components/RowsPerPageSelect.jsx";

// ── Export Modal ──────────────────────────────────────────────────────────────

const toDateInput = (d) => d.toISOString().slice(0, 10);

// Parse YYYY-MM-DD as local date (avoids UTC midnight → wrong day in IST)
const fmtDate = (str) => {
    if (!str) return "—";
    const [y, m, d] = str.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};
const defaultFrom = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return toDateInput(d);
};
const defaultTo = () => toDateInput(new Date());

const STATUS_OPTIONS = [
    { value: "pending", label: "Pending", color: "#b45309" },
    { value: "processing", label: "Processing", color: "#4f46e5" },
    { value: "delivered", label: "Delivered", color: "#16a34a" },
    { value: "cancelled", label: "Cancelled", color: "#b91c1c" },
    { value: "hold", label: "Hold", color: "#ea580c" },
];

// Status filter dropdown on the orders list — mirrors the dashboard's order-status cards
// so a card can deep-link straight into a matching filter.
const STATUS_TO_PARAM = {
    Pending: "pending",
    "Accounts Approved": "accounts_approved",
    Dispatched: "dispatched",
    "Out for Delivery": "out_for_delivery",
    Delivered: "delivered",
    "On Hold": "hold",
    Returned: "returned",
    Cancelled: "cancelled",
};
const PARAM_TO_STATUS = Object.fromEntries(
    Object.entries(STATUS_TO_PARAM).map(([label, val]) => [val, label]),
);
const STATUSES = ["All Status", ...Object.keys(STATUS_TO_PARAM)];

function ExportOrdersModal({ onClose }) {
    const [from, setFrom] = useState(defaultFrom);
    const [to, setTo] = useState(defaultTo);
    const [statuses, setStatuses] = useState(
        STATUS_OPTIONS.map((s) => s.value),
    );
    const [exporting, setExporting] = useState(false);

    const toggleStatus = (val) =>
        setStatuses((prev) =>
            prev.includes(val) ? prev.filter((s) => s !== val) : [...prev, val],
        );
    const allSelected = statuses.length === STATUS_OPTIONS.length;
    const noneSelected = statuses.length === 0;
    const maxDate = toDateInput(new Date());

    const handleExport = async () => {
        if (noneSelected) {
            alert("Select at least one status to export.");
            return;
        }
        setExporting(true);
        try {
            const params = {};
            if (from) params.from = from;
            if (to) params.to = to;
            if (!allSelected) params.status = statuses.join(",");

            const res = await ordersApi.exportOrders(params);
            const disposition = res.headers?.["content-disposition"] || "";
            const match = disposition.match(/filename="?([^";]+)"?/);
            const filename =
                match?.[1] || `orders_${from || "all"}_to_${to || "all"}.xlsx`;

            const url = URL.createObjectURL(
                new Blob([res.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                }),
            );
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
            onClose();
        } catch {
            alert("Export failed. Please try again.");
        } finally {
            setExporting(false);
        }
    };

    return (
        <div
            className="modal-overlay"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="modal pm-modal">
                <div className="modal-header">
                    <div className="pm-modal-title">
                        <div className="pm-modal-icon">
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                            >
                                <rect
                                    x="3"
                                    y="3"
                                    width="18"
                                    height="18"
                                    rx="2"
                                />
                                <path d="M3 9h18M9 21V9" />
                                <path d="m13 13 2 2-2 2" />
                                <path d="m15 15-2 2" />
                            </svg>
                        </div>
                        Export Orders
                    </div>
                    <button className="close-btn" onClick={onClose}>
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                <div className="pm-body">
                    <p className="export-desc">
                        Download orders as an Excel file (two sheets: order
                        summary + line items). Filter by date range and status.
                    </p>
                    <div className="export-date-row">
                        <div className="export-date-field">
                            <label
                                className="form-label"
                                style={{
                                    fontSize: 11,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    marginBottom: 4,
                                }}
                            >
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <rect
                                        x="3"
                                        y="4"
                                        width="18"
                                        height="18"
                                        rx="2"
                                    />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                                FROM
                            </label>
                            <input
                                type="date"
                                className="form-control"
                                style={{ fontSize: 12 }}
                                value={from}
                                max={to || maxDate}
                                onChange={(e) => setFrom(e.target.value)}
                            />
                        </div>
                        <div className="export-date-sep">—</div>
                        <div className="export-date-field">
                            <label
                                className="form-label"
                                style={{
                                    fontSize: 11,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    marginBottom: 4,
                                }}
                            >
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <rect
                                        x="3"
                                        y="4"
                                        width="18"
                                        height="18"
                                        rx="2"
                                    />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                                TO
                            </label>
                            <input
                                type="date"
                                className="form-control"
                                style={{ fontSize: 12 }}
                                value={to}
                                min={from || undefined}
                                max={maxDate}
                                onChange={(e) => setTo(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="export-status-section">
                        <div className="export-status-header">
                            <span className="export-status-label">Status</span>
                            <button
                                type="button"
                                className="export-status-toggle"
                                onClick={() =>
                                    setStatuses(
                                        allSelected
                                            ? []
                                            : STATUS_OPTIONS.map(
                                                  (s) => s.value,
                                              ),
                                    )
                                }
                            >
                                {allSelected ? "Deselect all" : "Select all"}
                            </button>
                        </div>
                        <div className="export-status-chips">
                            {STATUS_OPTIONS.map(({ value, label, color }) => {
                                const checked = statuses.includes(value);
                                return (
                                    <button
                                        key={value}
                                        type="button"
                                        className={`export-status-chip${checked ? " export-status-chip--on" : ""}`}
                                        style={
                                            checked
                                                ? {
                                                      borderColor: color,
                                                      background: color + "18",
                                                      color,
                                                  }
                                                : {
                                                      borderColor: color + "55",
                                                      color: color + "99",
                                                  }
                                        }
                                        onClick={() => toggleStatus(value)}
                                    >
                                        <span
                                            className="export-status-dot"
                                            style={{
                                                background: checked
                                                    ? color
                                                    : color + "55",
                                            }}
                                        />
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <p className="export-hint">
                        Leave both dates blank to export all orders regardless
                        of date.
                    </p>
                </div>

                <div className="pm-footer">
                    <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={onClose}
                        disabled={exporting}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn-export"
                        onClick={handleExport}
                        disabled={exporting}
                    >
                        {exporting ? (
                            <>
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    style={{
                                        animation: "spin 1s linear infinite",
                                    }}
                                >
                                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                </svg>
                                Exporting…
                            </>
                        ) : (
                            <>
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                Export Excel
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── helpers ──────────────────────────────────────────────────────────────────

function mapOrder(o) {
    const lead = o.Lead || o.lead || {};
    const creator = o.Creator || o.creator || null;
    const date = fmtDate(o.delivery_date);
    const amount =
        o.total_amount != null
            ? `₹${Number(o.total_amount).toLocaleString("en-IN")}`
            : "—";
    const statusMap = {
        delivered: "Delivered",
        pending: "Pending",
        cancelled: "Cancelled",
        hold: "On Hold",
    };
    return {
        id: o.id,
        customer: lead.name || o.lead_id || "—",
        groupRef: o.group_ref || null,
        isOrgOrder: !!o.group_ref,
        date,
        amount,
        rawAmount: Number(o.total_amount) || 0,
        status: statusMap[o.status] || o.status || "Pending",
        payment: o.payment_method || "—",
        orderType: o.order_type || "plain",
        productCategory: o.product_category || null,
        customerCategory: lead.customer_category || "general",
        lead_id: o.lead_id,
        invoiceNo: o.invoice_number || "—",
        placedBy: creator?.name || (o.placed_by ? `User #${o.placed_by}` : "—"),
        placedByRole: creator?.role?.name || "",
        salesExec: o.sales_executive || "",
        products: (o.items || []).map((i) => ({
            name: i.product?.name || "",
            qty: i.quantity || 0,
        })),
        _raw: o,
    };
}

// `options` is a list of { id, name } — selecting one calls onChange(id) so the filter
// matches a real users.id instead of the free-text sales_executive column (which could be
// an uppercase name or an email, and never matched reliably).
function SearchableSalesNameSelect({ value, options, onChange, loading }) {
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const filtered = options.filter(o => o.name.toLowerCase().includes(search.toLowerCase()));
    const selected = options.find(o => String(o.id) === String(value));
    return (
        <div style={{ position: 'relative', width: 190, flexShrink: 0 }}>
            <div className="form-control" onClick={() => !loading && setIsOpen(o => !o)}
                style={{ cursor: loading ? 'default' : 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, height: 32, padding: '0 10px' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selected ? 'inherit' : 'var(--text-muted)' }}>
                    {loading ? 'Loading…' : (selected?.name || 'All Executives')}
                </span>
                <span style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0, marginLeft: 4 }}>▼</span>
            </div>
            {isOpen && (
                <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setIsOpen(false)} />
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, zIndex: 50, maxHeight: 240, display: 'flex', flexDirection: 'column', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', minWidth: 210 }}>
                        <input autoFocus type="text" placeholder="Search name…" value={search} onChange={e => setSearch(e.target.value)}
                            style={{ padding: '8px 12px', border: 'none', borderBottom: '1px solid #f1f5f9', outline: 'none', fontSize: 13, borderRadius: '6px 6px 0 0', width: '100%', boxSizing: 'border-box' }} />
                        <div style={{ overflowY: 'auto', flex: 1, padding: 4 }}>
                            <div style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderRadius: 4, color: '#64748b', background: !value ? '#f1f5f9' : 'transparent' }}
                                onClick={() => { onChange(''); setIsOpen(false); setSearch(''); }}>All Executives</div>
                            {filtered.map(o => (
                                <div key={o.id} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderRadius: 4, color: '#334155', background: String(value) === String(o.id) ? '#f1f5f9' : 'transparent' }}
                                    onClick={() => { onChange(o.id); setIsOpen(false); setSearch(''); }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                    onMouseLeave={e => e.currentTarget.style.background = String(value) === String(o.id) ? '#f1f5f9' : 'transparent'}>
                                    {o.name}
                                </div>
                            ))}
                            {filtered.length === 0 && <div style={{ padding: '8px 12px', fontSize: 13, color: '#94a3b8' }}>No results</div>}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

const CAT_MAP = {
    local_retail: {
        label: "Local Retail",
        bg: "rgba(16,185,129,0.1)",
        color: "#065f46",
        border: "rgba(16,185,129,0.3)",
    },
    printed: {
        label: "Printed",
        bg: "rgba(99,102,241,0.1)",
        color: "#3730a3",
        border: "rgba(99,102,241,0.3)",
    },
    retail_big: {
        label: "Retail Big",
        bg: "rgba(245,158,11,0.1)",
        color: "#92400e",
        border: "rgba(245,158,11,0.3)",
    },
    inter_state_retail: {
        label: "Inter-State Retail",
        bg: "rgba(59,130,246,0.1)",
        color: "#1e40af",
        border: "rgba(59,130,246,0.3)",
    },
};

const CUSTOMER_CATEGORY_MAP = {
    general: {
        label: "General Customer",
        bg: "rgba(100,116,139,0.1)",
        color: "#334155",
        border: "rgba(100,116,139,0.3)",
    },
    ecommerce: {
        label: "Ecommerce",
        bg: "rgba(168,85,247,0.1)",
        color: "#6b21a8",
        border: "rgba(168,85,247,0.3)",
    },
    tender: {
        label: "Tender",
        bg: "rgba(234,88,12,0.1)",
        color: "#9a3412",
        border: "rgba(234,88,12,0.3)",
    },
};

// ── Component ─────────────────────────────────────────────────────────────────

const GitBranchIcon = () => (
    <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="6" y1="3" x2="6" y2="15" />
        <circle cx="18" cy="6" r="3" />
        <circle cx="6" cy="18" r="3" />
        <path d="M18 9a9 9 0 0 1-9 9" />
    </svg>
);

export default function OrdersPage({ role }) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const groupRefParam = searchParams.get("group_ref");
    const createdFromParam = searchParams.get("created_from");
    const createdToParam = searchParams.get("created_to");

    const userRole =
        role || JSON.parse(sessionStorage.getItem("user") || "{}").role;
    const isAdmin = userRole === "admin";

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showExport, setShowExport] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportMsg, setExportMsg] = useState(null); // { type: 'info'|'success'|'error', text }
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [statusFilter,    setStatusFilter]    = useState(
        PARAM_TO_STATUS[searchParams.get("status")] || "All Status",
    );
    const [productCategory,  setProductCategory]  = useState("");
    const [clientTypeFilter, setClientTypeFilter] = useState("");
    const [customerCategoryFilter, setCustomerCategoryFilter] = useState("");
    const [salesExecFilterId, setSalesExecFilterId] = useState("");
    const [salesExecOptions, setSalesExecOptions]   = useState([]); // [{ id, name }]
    const [salesNamesLoading, setSalesNamesLoading] = useState(false);
    const [dateFrom,         setDateFrom]         = useState(searchParams.get("date_from") || "");
    const [dateTo,          setDateTo]          = useState(searchParams.get("date_to") || "");
    const [dateBasis,       setDateBasis]        = useState("dispatch"); // 'dispatch' | 'created' — which date column dateFrom/dateTo filter on
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [totalRevenue, setTotalRevenue] = useState(null);
    const [statusCounts, setStatusCounts] = useState({});

    // Reset page when deep-link params change
    useEffect(() => {
        setPage(1);
    }, [groupRefParam, createdFromParam, createdToParam]);

    // Debounce search
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 400);
        return () => clearTimeout(t);
    }, [search]);

    const buildParams = useCallback(
        (extra = {}) => {
            const p = { sortBy: "created_at", sortOrder: "DESC", ...extra };
            if (debouncedSearch) p.search = debouncedSearch;
            if (statusFilter !== "All Status")
                p.status =
                    STATUS_TO_PARAM[statusFilter] || statusFilter.toLowerCase();
            if (productCategory)  p.product_category  = productCategory;
            if (clientTypeFilter) p.client_type        = clientTypeFilter;
            if (customerCategoryFilter) p.customer_category = customerCategoryFilter;
            if (salesExecFilterId) p.sales_executive_id = salesExecFilterId;
            if (dateFrom || dateTo) {
                if (dateBasis === "created") {
                    if (dateFrom) p.created_from = dateFrom;
                    if (dateTo) p.created_to = dateTo;
                } else {
                    if (dateFrom) p.date_from = dateFrom;
                    if (dateTo) p.date_to = dateTo;
                }
            }
            if (groupRefParam) p.group_ref = groupRefParam;
            if (createdFromParam) p.created_from = createdFromParam;
            if (createdToParam) p.created_to = createdToParam;
            return p;
        },
        [debouncedSearch, statusFilter, productCategory, clientTypeFilter, customerCategoryFilter, salesExecFilterId, dateFrom, dateTo, dateBasis, groupRefParam, createdFromParam, createdToParam],
    );

    const fetchOrders = useCallback(async () => {
        setLoading(true);
        try {
            // ordersApi.getAll already extracts r.data, so res = {success, count, data:[...], statusCounts:[...]}
            const res = await ordersApi.getAll(
                buildParams({ limit, page }),
            );
            const list = Array.isArray(res.data)
                ? res.data
                : Array.isArray(res)
                  ? res
                  : [];
            setOrders(list.map(mapOrder));
            setTotalPages(res.totalPages ?? 1);
            setTotalCount(res.count ?? list.length);
            setTotalRevenue(res.totalRevenue ?? null);
            if (Array.isArray(res.statusCounts)) {
                const c = {};
                res.statusCounts.forEach((s) => {
                    c[s.status] = s.count;
                });
                setStatusCounts(c);
            }
        } catch (err) {
            console.error("Failed to fetch orders:", err);
        } finally {
            setLoading(false);
        }
    }, [buildParams, page, limit]);

    const handleExport = async () => {
        setExporting(true);
        setExportMsg({ type: "info", text: "Preparing export…" });
        try {
            const params = buildParams();
            const res = await ordersApi.exportOrders(params);

            // Cached — file is ready immediately
            if (res.cached || res.status === 'done') {
                setExportMsg({ type: "info", text: "Downloading…" });
                await triggerDownload(res.jobId);
                setExportMsg({ type: "success", text: "Downloaded!" });
                setTimeout(() => setExportMsg(null), 3000);
                setExporting(false);
                return;
            }

            // Queued — poll until done
            const { jobId } = res;
            setExportMsg({ type: "info", text: "Export queued — generating file…" });
            let attempts = 0;
            const maxAttempts = 30;
            const poll = setInterval(async () => {
                try {
                    attempts++;
                    const status = await ordersApi.getExportStatus(jobId);
                    if (status.status === 'done') {
                        clearInterval(poll);
                        setExportMsg({ type: "info", text: "Downloading…" });
                        await triggerDownload(jobId);
                        setExportMsg({ type: "success", text: "Downloaded!" });
                        setTimeout(() => setExportMsg(null), 3000);
                        setExporting(false);
                    } else if (status.status === 'failed' || attempts >= maxAttempts) {
                        clearInterval(poll);
                        const msg = status.status === 'failed' ? 'Export failed on server.' : 'Export timed out.';
                        setExportMsg({ type: "error", text: msg });
                        setTimeout(() => setExportMsg(null), 5000);
                        setExporting(false);
                    }
                } catch {
                    clearInterval(poll);
                    setExportMsg({ type: "error", text: "Status check failed." });
                    setTimeout(() => setExportMsg(null), 5000);
                    setExporting(false);
                }
            }, 2000);
        } catch {
            setExportMsg({ type: "error", text: "Export failed. Please try again." });
            setTimeout(() => setExportMsg(null), 5000);
            setExporting(false);
        }
    };

    const triggerDownload = async (jobId) => {
        const blob = await ordersApi.downloadExport(jobId);
        const disposition = blob.headers?.["content-disposition"] || "";
        const match = disposition.match(/filename="?([^";]+)"?/);
        const filename = match?.[1] || "orders_export.xlsx";
        const url = URL.createObjectURL(new Blob([blob.data]));
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
    };

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    // Load sales executives for the filter dropdown, keyed by their real user id
    useEffect(() => {
        setSalesNamesLoading(true);
        Promise.all([
            usersApi.getAll({ role: 'sales',    limit: 200 }),
            usersApi.getAll({ role: 'sales_tl', limit: 200 }),
            usersApi.getAll({ role: 'crm',      limit: 200 }),
            usersApi.getAll({ role: 'crm_manager', limit: 200 }),
        ]).then(results => {
            const all = results.flatMap(r => r.data ?? r).filter(u => u.is_active !== false);
            const byId = new Map();
            all.forEach(u => { if (u.id && u.name && !byId.has(u.id)) byId.set(u.id, { id: u.id, name: u.name }); });
            setSalesExecOptions([...byId.values()].sort((a, b) => a.name.localeCompare(b.name)));
        }).catch(console.error).finally(() => setSalesNamesLoading(false));
    }, []);

    const handleDelete = async (orderId) => {
        if (
            !window.confirm(
                `Are you sure you want to delete order #${orderId}?`,
            )
        )
            return;
        try {
            await ordersApi.remove(orderId);
            fetchOrders();
        } catch (err) {
            alert(err.message || "Failed to delete order.");
        }
    };

    const clearDates = () => {
        setDateFrom("");
        setDateTo("");
        setPage(1);
    };

    const clearGroupRef = () => {
        const p = new URLSearchParams(searchParams);
        p.delete("group_ref");
        setSearchParams(p);
    };

    const clearCreatedRange = () => {
        const p = new URLSearchParams(searchParams);
        p.delete("created_from");
        p.delete("created_to");
        setSearchParams(p);
    };

    const inputStyle = {
        fontSize: 12,
        padding: "6px 10px",
        borderRadius: 6,
        border: "1px solid var(--border)",
        background: "var(--card)",
        color: "var(--text)",
        fontFamily: "inherit",
        cursor: "pointer",
        height: 34,
    };

    const countForStatus = (s) => {
        if (s === "All Status")
            return Object.values(statusCounts).reduce((a, b) => a + b, 0);
        const key = STATUS_TO_PARAM[s] || s.toLowerCase();
        return statusCounts[key] ?? 0;
    };

    return (
        <div className="page-body">
            <div className="card">
                {/* Header */}
                <div className="card-header">
                    <div>
                        <span className="card-title">
                            {groupRefParam ? "Org Group Orders" : "Orders"}
                        </span>
                        {groupRefParam && (
                            <div
                                style={{
                                    fontSize: 11,
                                    color: "var(--text-muted)",
                                    marginTop: 2,
                                    fontFamily: "monospace",
                                }}
                            >
                                group {groupRefParam.slice(0, 8)}…
                            </div>
                        )}
                    </div>
                    <div
                        style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                        }}
                    >
                        {exportMsg && (
                            <span style={{
                                fontSize: 12, fontWeight: 500, padding: "4px 10px", borderRadius: 6,
                                background: exportMsg.type === "success" ? "rgba(22,163,74,0.1)" : exportMsg.type === "error" ? "rgba(220,38,38,0.1)" : "rgba(59,130,246,0.1)",
                                color: exportMsg.type === "success" ? "#16a34a" : exportMsg.type === "error" ? "#dc2626" : "#2563eb",
                                border: `1px solid ${exportMsg.type === "success" ? "rgba(22,163,74,0.25)" : exportMsg.type === "error" ? "rgba(220,38,38,0.25)" : "rgba(59,130,246,0.25)"}`,
                                display: "flex", alignItems: "center", gap: 5,
                            }}>
                                {exportMsg.type === "info" && (
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: "spin 1s linear infinite" }}>
                                        <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                                    </svg>
                                )}
                                {exportMsg.text}
                            </span>
                        )}
                        {isAdmin && (
                            <button
                                className="btn btn-outline btn-sm"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 5,
                                    fontSize: 12,
                                }}
                                onClick={handleExport}
                                disabled={exporting}
                            >
                                <svg
                                    width="13"
                                    height="13"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                {exporting ? "Exporting…" : "Export"}
                            </button>
                        )}
                        <button
                            className="btn btn-primary btn-sm"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: 12,
                            }}
                            onClick={() => navigate("/place-order")}
                        >
                            <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                            >
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            Place Order
                        </button>
                    </div>
                </div>

                <div className="card-body">
                    {/* ── Filter panel ─────────────────────────────────────── */}
                    {(() => {
                        const anyActive = statusFilter !== "All Status" || dateFrom || dateTo || productCategory || clientTypeFilter || customerCategoryFilter || salesExecFilterId || search;
                        const activeCount = [statusFilter !== "All Status" && statusFilter, dateFrom, dateTo, productCategory, clientTypeFilter, customerCategoryFilter, salesExecFilterId, search].filter(Boolean).length;
                        const clearAll = () => { setStatusFilter("All Status"); setDateFrom(""); setDateTo(""); setDateBasis("dispatch"); setProductCategory(""); setClientTypeFilter(""); setCustomerCategoryFilter(""); setSalesExecFilterId(""); setSearch(""); setPage(1); };

                        const LBL = ({ children }) => (
                            <span style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", whiteSpace: "nowrap" }}>{children}</span>
                        );
                        const ClearBtn = ({ show, onClick }) => show ? (
                            <button onClick={onClick} style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1", fontSize: 14, lineHeight: 1, padding: "0 0 0 4px" }}>×</button>
                        ) : null;

                        return (
                            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 16px", marginBottom: 20 }}>
                                {/* header */}
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: "#475569", letterSpacing: "0.01em" }}>Filters</span>
                                        {anyActive && <span style={{ background: "#3b82f6", color: "#fff", fontSize: 10, fontWeight: 700, borderRadius: 10, padding: "1px 7px" }}>{activeCount}</span>}
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        {groupRefParam && (
                                            <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(22,163,74,0.08)", color: "#16a34a", border: "1px solid rgba(22,163,74,0.25)", padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
                                                <GitBranchIcon /> Org Group: {groupRefParam.slice(0, 8)}…
                                                <button onClick={clearGroupRef} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>✕</button>
                                            </div>
                                        )}
                                        {(createdFromParam || createdToParam) && (
                                            <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(37,99,235,0.08)", color: "#2563eb", border: "1px solid rgba(37,99,235,0.25)", padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
                                                Created: {fmtDate(createdFromParam)} – {fmtDate(createdToParam)}
                                                <button onClick={clearCreatedRange} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>✕</button>
                                            </div>
                                        )}
                                        {!loading && <span style={{ fontSize: 12, color: "#94a3b8" }}>{totalCount} orders</span>}
                                        {!loading && totalRevenue !== null && (
                                            <span style={{ fontSize: 12, fontWeight: 600, color: "#16a34a", background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.2)", borderRadius: 6, padding: "2px 8px" }}>
                                                Revenue (Delivered): ₹{totalRevenue.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                                            </span>
                                        )}
                                        {anyActive && (
                                            <button onClick={clearAll} style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 6, cursor: "pointer", color: "#64748b", fontSize: 12, fontWeight: 500, padding: "3px 10px", display: "flex", alignItems: "center", gap: 5 }}>
                                                <span style={{ fontSize: 14, lineHeight: 1 }}>×</span> Clear all
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* filter controls */}
                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>

                                    {/* Status */}
                                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                        <LBL>Status</LBL>
                                        <select style={{ ...inputStyle, width: 170 }} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                                            {STATUSES.map(s => (
                                                <option key={s} value={s}>{s}{statusCounts ? ` (${countForStatus(s)})` : ""}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Date From */}
                                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            <LBL>{dateBasis === "created" ? "Order Date From" : "Dispatch From"}</LBL>
                                            <ClearBtn show={!!dateFrom} onClick={() => { setDateFrom(""); setPage(1); }} />
                                        </div>
                                        <input type="date" style={inputStyle} value={dateFrom} max={dateTo || undefined} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
                                    </div>

                                    {/* Date To */}
                                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            <LBL>{dateBasis === "created" ? "Order Date To" : "Dispatch To"}</LBL>
                                            <ClearBtn show={!!dateTo} onClick={() => { setDateTo(""); setPage(1); }} />
                                        </div>
                                        <input type="date" style={inputStyle} value={dateTo} min={dateFrom || undefined} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
                                    </div>

                                    {/* Date basis — which date column the range above filters on */}
                                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                        <LBL>Filter Date By</LBL>
                                        <select style={{ ...inputStyle, width: 170 }} value={dateBasis} onChange={(e) => { setDateBasis(e.target.value); setPage(1); }}>
                                            <option value="dispatch">Dispatch Date</option>
                                            <option value="created">Order Create Date</option>
                                        </select>
                                    </div>

                                    {/* Product Type */}
                                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            <LBL>Product Type</LBL>
                                            <ClearBtn show={!!productCategory} onClick={() => { setProductCategory(""); setPage(1); }} />
                                        </div>
                                        <select style={{ ...inputStyle, width: 160 }} value={productCategory} onChange={(e) => { setProductCategory(e.target.value); setPage(1); }}>
                                            <option value="">All Types</option>
                                            <option value="local_retail">Local Retail</option>
                                            <option value="printed">Printed</option>
                                            <option value="retail_big">Retail Big</option>
                                            <option value="inter_state_retail">Inter-State Retail</option>
                                        </select>
                                    </div>

                                    {/* Client Type */}
                                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            <LBL>Client Type</LBL>
                                            <ClearBtn show={!!clientTypeFilter} onClick={() => { setClientTypeFilter(""); setPage(1); }} />
                                        </div>
                                        <select style={{ ...inputStyle, width: 180 }} value={clientTypeFilter} onChange={(e) => { setClientTypeFilter(e.target.value); setPage(1); }}>
                                            <option value="">All Client Types</option>
                                            <option value="Place Order">Place Order</option>
                                            <option value="New Client Onboarding">New Client Onboarding</option>
                                        </select>
                                    </div>

                                    {/* Customer Category */}
                                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            <LBL>Customer Category</LBL>
                                            <ClearBtn show={!!customerCategoryFilter} onClick={() => { setCustomerCategoryFilter(""); setPage(1); }} />
                                        </div>
                                        <select style={{ ...inputStyle, width: 170 }} value={customerCategoryFilter} onChange={(e) => { setCustomerCategoryFilter(e.target.value); setPage(1); }}>
                                            <option value="">All Categories</option>
                                            <option value="general">General Customer</option>
                                            <option value="ecommerce">Ecommerce</option>
                                            <option value="tender">Tender</option>
                                        </select>
                                    </div>

                                    {/* Sales Executive */}
                                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            <LBL>Sales Executive</LBL>
                                            <ClearBtn show={!!salesExecFilterId} onClick={() => { setSalesExecFilterId(""); setPage(1); }} />
                                        </div>
                                        <SearchableSalesNameSelect value={salesExecFilterId} options={salesExecOptions} onChange={(id) => { setSalesExecFilterId(id); setPage(1); }} loading={salesNamesLoading} />
                                    </div>

                                    {/* Search */}
                                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            <LBL>Search</LBL>
                                            <ClearBtn show={!!search} onClick={() => { setSearch(""); setPage(1); }} />
                                        </div>
                                        <div className="search-bar" style={{ width: 230, flexShrink: 0 }}>
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                                            <input placeholder="Customer or order ID…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
                                        </div>
                                    </div>

                                </div>
                            </div>
                        );
                    })()}

                    {/* Table */}
                    {loading ? (
                        <div
                            style={{
                                textAlign: "center",
                                padding: "40px",
                                color: "var(--text-muted)",
                                fontSize: 13,
                            }}
                        >
                            Loading orders…
                        </div>
                    ) : (
                        <div className="table-wrapper">
                            <table>
                                <thead>
                                    <tr>
                                        <th style={{ fontSize: 12 }}>
                                            Order ID
                                        </th>
                                        <th style={{ fontSize: 12 }}>
                                            Customer
                                        </th>
                                        <th style={{ fontSize: 12 }}>
                                            Products
                                        </th>
                                        <th style={{ fontSize: 12 }}>Qty</th>
                                        <th style={{ fontSize: 12 }}>
                                            Product Category
                                        </th>
                                        <th style={{ fontSize: 12 }}>
                                            Customer Category
                                        </th>
                                        <th style={{ fontSize: 12 }}>Amount</th>
                                        <th style={{ fontSize: 12 }}>Status</th>
                                        <th style={{ fontSize: 12 }}>Sales Executive</th>
                                        <th style={{ fontSize: 12 }}>
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.length === 0 ? (
                                        <tr>
                                            <td
                                                colSpan={11}
                                                style={{
                                                    textAlign: "center",
                                                    color: "var(--text-muted)",
                                                    padding: "28px",
                                                    fontSize: 13,
                                                }}
                                            >
                                                No orders found
                                            </td>
                                        </tr>
                                    ) : (
                                        orders.map((o) => (
                                            <tr
                                                key={o.id}
                                                style={{ cursor: "pointer" }}
                                                onClick={() =>
                                                    navigate(`/orders/${o.id}`)
                                                }
                                            >
                                                <td
                                                    style={{
                                                        fontWeight: 700,
                                                        color: "var(--primary)",
                                                        fontSize: 13,
                                                    }}
                                                >
                                                    #{o.id}
                                                </td>
                                                <td>
                                                    <div
                                                        style={{
                                                            fontWeight: 600,
                                                            fontSize: 13,
                                                        }}
                                                    >
                                                        {o.customer}
                                                    </div>
                                                    {o.isOrgOrder && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(
                                                                    `/orders?group_ref=${o.groupRef}`,
                                                                );
                                                            }}
                                                            style={{
                                                                marginTop: 3,
                                                                display:
                                                                    "inline-flex",
                                                                alignItems:
                                                                    "center",
                                                                gap: 3,
                                                                fontSize: 10,
                                                                fontWeight: 600,
                                                                padding:
                                                                    "2px 6px",
                                                                background:
                                                                    "rgba(22,163,74,0.1)",
                                                                color: "#16a34a",
                                                                border: "1px solid rgba(22,163,74,0.25)",
                                                                borderRadius: 4,
                                                                cursor: "pointer",
                                                            }}
                                                        >
                                                            <GitBranchIcon />{" "}
                                                            Org Group
                                                        </button>
                                                    )}
                                                </td>
                                                <td style={{ maxWidth: 220 }}>
                                                    {o.products.length === 0 ? (
                                                        <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>
                                                    ) : (
                                                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                            {o.products.map((p, i) => (
                                                                <div key={i} style={{ fontSize: 12, padding: "2px 5px", borderRadius: 4, background: "transparent", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#1e293b", fontWeight: 500 }}>
                                                                    {p.name}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    {o.products.length === 0 ? (
                                                        <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>
                                                    ) : (
                                                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                            {o.products.map((p, i) => (
                                                                <div key={i} style={{ fontSize: 12, padding: "2px 5px", borderRadius: 4, background: "transparent", color: "#64748b", fontWeight: 500, textAlign: "right" }}>
                                                                    {p.qty}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    {(() => {
                                                        const cat =
                                                            CAT_MAP[
                                                                o
                                                                    .productCategory
                                                            ];
                                                        return cat ? (
                                                            <span
                                                                style={{
                                                                    display:
                                                                        "inline-block",
                                                                    padding:
                                                                        "2px 8px",
                                                                    borderRadius: 12,
                                                                    fontSize: 11,
                                                                    fontWeight: 700,
                                                                    whiteSpace:
                                                                        "nowrap",
                                                                    background:
                                                                        cat.bg,
                                                                    color: cat.color,
                                                                    border: `1px solid ${cat.border}`,
                                                                }}
                                                            >
                                                                {cat.label}
                                                            </span>
                                                        ) : (
                                                            <span
                                                                style={{
                                                                    color: "var(--text-muted)",
                                                                    fontSize: 11,
                                                                }}
                                                            >
                                                                —
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td>
                                                    {(() => {
                                                        const cat = CUSTOMER_CATEGORY_MAP[o.customerCategory];
                                                        return cat ? (
                                                            <span
                                                                style={{
                                                                    display: "inline-block",
                                                                    padding: "2px 8px",
                                                                    borderRadius: 12,
                                                                    fontSize: 11,
                                                                    fontWeight: 700,
                                                                    whiteSpace: "nowrap",
                                                                    background: cat.bg,
                                                                    color: cat.color,
                                                                    border: `1px solid ${cat.border}`,
                                                                }}
                                                            >
                                                                {cat.label}
                                                            </span>
                                                        ) : (
                                                            <span style={{ color: "var(--text-muted)", fontSize: 11 }}>—</span>
                                                        );
                                                    })()}
                                                </td>
                                                <td
                                                    style={{
                                                        fontWeight: 700,
                                                        fontSize: 13,
                                                    }}
                                                >
                                                    {o.amount}
                                                </td>
                                                <td>
                                                    <span
                                                        className={`badge badge-${
                                                            o.status ===
                                                            "Delivered"
                                                                ? "success"
                                                                : o.status ===
                                                                    "Cancelled"
                                                                  ? "danger"
                                                                  : o.status ===
                                                                      "On Hold"
                                                                    ? "gray"
                                                                    : "warning"
                                                        }`}
                                                        style={{ fontSize: 11 }}
                                                    >
                                                        {o.status}
                                                    </span>
                                                </td>
                                                <td style={{ fontSize: 12 }}>
                                                    {o.salesExec || <span style={{ color: "var(--text-muted)" }}>—</span>}
                                                </td>
                                                <td
                                                    style={{
                                                        display: "flex",
                                                        gap: 5,
                                                    }}
                                                    onClick={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                >
                                                    <button
                                                        className="btn btn-outline btn-xs"
                                                        style={{ fontSize: 11 }}
                                                        onClick={() =>
                                                            navigate(
                                                                `/orders/${o.id}`,
                                                            )
                                                        }
                                                    >
                                                        View
                                                    </button>
                                                    <button
                                                        className="btn btn-primary btn-xs"
                                                        style={{ fontSize: 11 }}
                                                        onClick={() =>
                                                            navigate(
                                                                `/place-order?lead_id=${o.lead_id}`,
                                                            )
                                                        }
                                                    >
                                                        Reorder
                                                    </button>
                                                    {isAdmin && (
                                                        <button
                                                            className="btn btn-danger btn-xs"
                                                            style={{
                                                                fontSize: 11,
                                                            }}
                                                            onClick={() =>
                                                                handleDelete(
                                                                    o.id,
                                                                )
                                                            }
                                                        >
                                                            Delete
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && orders.length > 0 && (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 16,
                                marginTop: 20,
                            }}
                        >
                            <button
                                className="btn btn-outline btn-sm"
                                onClick={() =>
                                    setPage((p) => Math.max(p - 1, 1))
                                }
                                disabled={page === 1}
                                style={{
                                    opacity: page === 1 ? 0.5 : 1,
                                    fontSize: 12,
                                }}
                            >
                                Previous
                            </button>
                            <span
                                style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: "var(--text-muted)",
                                }}
                            >
                                Page {page} of {totalPages} · {totalCount}{" "}
                                entries
                            </span>
                            <button
                                className="btn btn-outline btn-sm"
                                onClick={() =>
                                    setPage((p) => Math.min(p + 1, totalPages))
                                }
                                disabled={page >= totalPages}
                                style={{
                                    opacity: page >= totalPages ? 0.5 : 1,
                                    fontSize: 12,
                                }}
                            >
                                Next
                            </button>
                            <RowsPerPageSelect
                                value={limit}
                                onChange={(n) => { setLimit(n); setPage(1); }}
                            />
                        </div>
                    )}
                </div>
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}
